import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, throwError, timer } from 'rxjs';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { ArticoliList, ProductsList } from '../interfaces/list';
import { ColorFilterItem, Filter, FilterItem, SerializedFilterValues } from '../interfaces/filter';
import { Articolo, ArticoloFull } from '../interfaces/custom/articolo';
import { catchError, filter, groupBy, map, mergeAll, mergeMap, min, take, tap, toArray } from 'rxjs/operators';
import { AccountService } from '../services/custom/account.service';
import { api_base_url } from './api_url';
import { StoreService } from '../services/store.service';
import { Documento } from '../interfaces/custom/documento';
import { PriceArticoloService } from '../services/custom/price-articolo.service';
import { CategoriaAttualeService } from '../services/custom/categoria-attuale.service';
import { getItemsForFilter } from '../helpers/filter_builder';
import { scaleImg } from '../helpers/scale_img';
import { Collection } from '../interfaces/custom/collection';
import { AnalyticsService } from '../services/analytics.service.';
import { AnagraficaConf } from '../interfaces/custom/confezioni';
import { DynamicElement } from '../interfaces/custom/dynamic_element';
import { ShippingCostService } from '../services/custom/shipping-cost.service';
import { ServizioMenuService } from '../services/servizio-menu.service';

export interface ListOptions {
    search?: string
    page?: number;
    limit?: number;
    sort?: string;
    filterValues?: SerializedFilterValues;
}

type ArticoliMap = {
    [key: string]: ArticoloFull;
}

interface FilterListValueDef {
    slug: string;
    name: string;
}
type FilterValueDef = number | FilterListValueDef[];

interface ProductsForFilterValuesResult {
    [filterValueSlug: string]: number;
}

@Injectable({
    providedIn: 'root'
})
export class ShopService {
    constructor(
        private http: HttpClient, private account: AccountService,
        private store: StoreService,
        private priceArticolo: PriceArticoloService,
        private shippingCost: ShippingCostService,
        private categoria: CategoriaAttualeService,
        private analytics: AnalyticsService,
        private servizioMenu: ServizioMenuService
    ) {}

    getAllSpedizioni() {
        return this.http.get<any>(api_base_url + 'InfoSpedizioni', { headers: this.getBusinessHeader() }).pipe(
            map(arr => arr.filter(obj => obj.canale == this.store.canale)),
            map(arr => {
                let groupped = {0: [], 1: [], 2: []};
                arr.forEach(el => groupped[el.tipo_spedizione].push(el));
                return arr;
            })
        );
    }

    getInfoSpedizioni(address) {
        return this.http.post<any>(api_base_url + `SpeseSpedizione/${this.store.azienda_nome_univoco}/info`,
        address,
        {
            headers: this.account.getHeader()
        }).pipe(
            map(arr => arr.filter(obj => obj.canale == this.store.canale)),
        );
    }

    getAddressByAddressLine(address) {
        return this.http.get<any>(api_base_url + 'GoogleService/address', {
            headers: this.account.getHeader(),
            params: {
                'address': address,
            }
        });
    }

    getAddressByPos(lat, lon) {
        return this.http.get<any>(api_base_url + 'GoogleService/reverse', {
            headers: this.account.getHeader(),
            params: {
                'latitudine': lat,
                'longitudine': lon
            }
        });
    }
    
    getBusinessHeader() {
        return {
            ...this.account.getHeader(),
            'Business': this.store.azienda_nome_univoco,
            ...this.store.x_canale
        }
    }

    getShopInfo() {
        return this.http.get<any>(api_base_url + `Azienda/informazioni`, {
            headers: {
                ...this.getBusinessHeader()
            }
        });
    }

    getConfezioniDesc() {
        return this.http.get<AnagraficaConf[]>(api_base_url + `ConfezioniAziendali/`, {
            headers: {
                ...this.getBusinessHeader()
            }
        });
    }

    getPolicy(type) {
        return this.http.get<any>(api_base_url + `Legal/${type}`, {
            headers: {
                ...this.getBusinessHeader()
            }
        });
    }

    getCondizioniDiPagamento() {
        return this.http.get<any>(api_base_url + `CondizioniPagamento`, {
            headers: {
                ...this.getBusinessHeader()
            }
        }).pipe(	
            map(arr => arr.filter(obj => obj.canale == this.store.canale))	
        )
    }

    getSpedizioniDisponibili(documento, subtotal) {
        return this.http.post<any[]>(api_base_url + 'SpeseSpedizione', documento, {
            headers: {
                ...this.getBusinessHeader(),
                ...this.store.x_canale
            }
        }).pipe(
            map(arr => arr.filter(obj => obj.canale == this.store.canale)),
            mergeAll(),
            groupBy(obj => obj.tipo_spedizione),
            mergeMap(group => group.pipe(
                min((a,b) => this.shippingCost.getPriceOfShipping(a, subtotal) - this.shippingCost.getPriceOfShipping(b, subtotal))
            )),
            toArray(),
            map(arr => arr.sort((a,b) => a.tipo_spedizione - b.tipo_spedizione))
        );
    }

    getFasce(codiceDestinazione) {
        return this.http.get<any>(api_base_url + 'FasceConsegna/' + codiceDestinazione + '/Disponibili', {
            headers: {
                ...this.getBusinessHeader()
            }
        });
    }

    getStripeIntent(ordine) {
        return this.http.post<any>(api_base_url + 'Documenti/Stripe/Order', ordine, {
            headers: {
                ...this.getBusinessHeader(),
                ...this.store.x_canale
            }
        });
    }

    retrivePayment(numreg, payment_code) {
        return this.http.get<any>(api_base_url + `Documenti/stripe/retrivepayment/${numreg}/${payment_code}`, {
            headers: {
                ...this.getBusinessHeader(),
                ...this.store.x_canale
            },
        }); 
    }

    creaOrdine(ordine) {
        return this.http.post<any>(api_base_url + 'Documenti', ordine, {
            headers: {
                ...this.getBusinessHeader(),
                ...this.store.x_canale
            }
        });
    }

    getCodiceSconto(codice, totale) {
        return this.http.get<any>(api_base_url + 'Coupon', {
            headers: {
                ...this.getBusinessHeader(),
            },
            params: {
                'codice_coupon': codice,
                'valore_min': totale
            }
        });
    }

    getArticoliHeader(): any {
        let header = this.getBusinessHeader();
        delete header['Authorization-Type'];
        Object.assign(header, this.store.x_canale);
        return header;
    }

    getArticoliForCarousel(type : 'available' | 'latest' | 'promo', mode : 'category' | 'products',
        categoria, products, limit) : Observable<Articolo[]> {
        let extra_params = {};
        if (mode == 'category')
            if (categoria?.codicefamiglia)
                extra_params['famiglia'] = categoria.codicefamiglia;

        extra_params['page_result'] = limit || 30;
        extra_params['disponibili'] = true;

        if (type == 'available') {
            if (mode == 'products') {
                return of([]);
            }
            else {
                return this.http.get<Articolo[]>(api_base_url + 'Articoli', {
                    headers: this.getArticoliHeader(),
                    params: {
                        ...extra_params,
                        'order_by': 'random'
                    }
                });
            }
        }
        else if (type == 'latest') {
            return this.http.get<Articolo[]>(api_base_url + 'Articoli', {
                headers: this.getArticoliHeader(),
                params: {
                    ...extra_params,
                    'order_by': '<data_creazione',
                }
            })
        }
        else {
            return this.http.get<Articolo[]>(api_base_url + 'Articoli/Promo', {
                headers: this.getArticoliHeader(),
                params: {
                    ...extra_params,
                    'order_by': 'random',
                }
            });
        }
        
    }

    getArticoliRandom() {
        return this.http.get<Articolo[]>(api_base_url + 'Articoli', {
            headers: this.getArticoliHeader(),
            params: {
                'page_result': '25',
                'order_by': 'random',
                'disponibili': 'true'
            }
        }).pipe(catchError(err => of([])));
    }

    getNuoviArticoli(): Observable<Articolo[]> {
        return this.http.get<Articolo[]>(api_base_url + 'Articoli', {
            headers: this.getArticoliHeader(),
            params: {
                'page_result': '20',
                'order_by': '<data_creazione',
                'disponibili': 'true'
            }
        })
    }

    getArticoliPromo(): Observable<Articolo[]> {
        return this.http.get<Articolo[]>(api_base_url + 'Articoli/Promo', {
            headers: this.getArticoliHeader(),
            params: {
                'page_result': '20',
                'order_by': 'random',
                'disponibili': 'true'
            }
        });
    }

    getArticoliInEvidenza() {
        return this.http.get<Articolo[]>(api_base_url + 'Articoli', {
            headers: this.getArticoliHeader(),
            params: {
                'prodotto_in_evidenza': 'true',
                'page_result': '200',
                'order_by': 'random',
                'disponibili': 'true'
            }
        })
    }

    getArticoliPiuAcquistati() : Observable<Articolo[]> {
        return this.http.get<Articolo[]>(api_base_url + 'ArticoliFrequenti', {
            headers: this.getArticoliHeader(),
        })
    }

    getArticolo(codart): Observable<ArticoloFull> {
        return this.http.get<ArticoloFull>(api_base_url + `Articoli/${codart}`, {
            headers: this.getArticoliHeader()
        });
    }

    getArticoliDiOrdine(ordine: Documento): Observable<ArticoliMap> {
        let bunch = {};
        for (let rigaCorpo of ordine.corpo) {
            if (rigaCorpo.opzioni?.length) {
                bunch[rigaCorpo.codiceArticolo] = this.http.get<ArticoloFull>(api_base_url + 'Articoli/'
                        + rigaCorpo.codiceArticolo, {
                    headers: this.getBusinessHeader()
                });
            }
        }

        if (Object.keys(bunch).length == 0) {
            bunch = of({});
        }

        return forkJoin(bunch);
    }

    getArticoliDiOrdineTutti(numreg) : Observable<ArticoloFull[]> {
        return this.http.get<ArticoloFull[]>(api_base_url + 'Documenti/riordino?numreg=' + numreg, {
            headers: this.getArticoliHeader()
        });
    }

    getArticoliTutti(): Observable<ArticoloFull[]> {
        return this.http.get<ArticoloFull[]>(api_base_url + 'Articoli', {
            headers: this.getArticoliHeader(),
            params: {
                'page_result': '300',
                'disponibili': 'true'
            }
        });
    }

    getArticoliComposizione(codart): Observable<ArticoloFull[]> {
        return this.http.get<ArticoloFull[]>(api_base_url + `Articoli/${codart}/ArticoliRicetta`, {
            headers: this.getArticoliHeader()
        }).pipe(
            catchError(err => {
                return throwError(`Articolo errato! Uno o più articoli presenti nel box sono stati eliminati o non sono attivi. Impossibile procedere con l'acquisto.`);
            })
        )
    }


    getArticoliList(categoria, options: ListOptions): Observable<ArticoliList> {
        return this.getExtraFields().pipe(
            mergeMap(extra_fields => {
                const page = options.page || 1;
                const limit = options.limit || this.store.columns * 3;
                const sort = options.sort || 'default';
                const filterValues = options.filterValues || {};
                const filters: Filter[] = [];
                const filtersDef = [
                    { type: 'check', slug: 'other', name: 'Altri' },
                    { type: 'range', slug: 'price', name: 'Prezzo' },
                    { type: 'radio', slug: 'marca', name: 'Marca'},
                ];

                let extra_params = {
                    'page': page,
                    'page_result': limit,
                }

                if ('marca' in filterValues) {
                    extra_params['marca'] = filterValues['marca'];
                }

                let filtersByCustomList = extra_fields.filter(f => f.items);
                for (let f of filtersByCustomList) {
                    if (f.slug in filterValues)
                        extra_params[f.slug] = filterValues[f.slug];
                }

                if (sort == 'price_asc')
                    extra_params['order_by'] = '>prezzo_originale,codart';
                else if (sort == 'price_desc')
                    extra_params['order_by'] = '<prezzo_originale,codart';
                else {
                    extra_params['order_by'] = 'famiglia,sottofamiglia,gruppo,sottogruppo,marca,prezzo_originale,codart';
                }

                let filtersCheckbox = extra_fields.filter(f => f.type == 'other');

                if ('other' in filterValues) {
                    let others = filterValues['other'].split(',');
                    if (others.includes('promo'))
                        extra_params['promo'] = true;
                    if (others.includes('disponibili'))
                        extra_params['disponibili'] = true;
                    if (others.includes('in_evidenza'))
                        extra_params['prodotto_in_evidenza'] = true;
                    for (let f of filtersCheckbox) {
                        if (others.includes(f.slug))
                            extra_params[f.slug] = 1;
                    }
                }

                let getNum = (num) => Number(num) || 0; 
                if (filterValues['price']) {
                    let [min, max] = filterValues['price'].split('-');
                    const KEY = '[prezzo_originale]';
                    extra_params[KEY] = [];
                    if (min && !Number.isNaN(Number(min)))
                        extra_params[KEY].push('>' + (getNum(min) - 0.001));
                    if (max && !Number.isNaN(Number(max)))
                        extra_params[KEY].push('<' + (getNum(max) + 0.001));
                }

                let filtersByRange = extra_fields.filter(f => f.type == 'range');
                for (let f of filtersByRange) {
                    if (filterValues[f.slug]) {
                        let [min, max] = filterValues[f.slug].split('-')
                        const KEY = `[${f.slug}]`;
                        extra_params[KEY] = [];
                        if (min && !Number.isNaN(Number(min)))
                            extra_params[KEY].push('>' + (getNum(min) - (f.int ? 1 : 0.001)));
                        if (max && !Number.isNaN(Number(max)))
                            extra_params[KEY].push('<' + (getNum(max) + (f.int ? 1 : 0.001)));
                    }
                }

                let search = filterValues['search'];
                search = search ? search.trim() : '';

                let filtersByText = extra_fields.filter(f => f.type == 'text');
                for (let f of filtersByText) {
                    if (filterValues[f.slug]) {
                        extra_params[`[${f.slug}]`] = `#${filterValues[f.slug]}#`;
                    }
                }

                let articoli$ = this.getArticoli(categoria, options.search, extra_params);
                let marche$ = this.getMarche(categoria);

                return forkJoin([this.categoria.categorie$.pipe(take(1)), articoli$, marche$])
                    .pipe(map<any, ArticoliList>(r => {
                        let categorie = r[0];
                        let items = r[1].body || [];
                        let totalCount = 0;
                        try {
                            totalCount = r[1].headers.get('x-totalcount') || 0;
                        }
                        catch (err) { }

                        if (options.search)
                            this.analytics.mtRicerca(options.search, totalCount);

                        let name_categorie = 'Categorie'
                        if (this.store.main_page == 'menu')
                            name_categorie = 'Menu'

                        if (categoria == null) {
                            filters.push({
                                type: 'categories',
                                slug: 'categories',
                                name: name_categorie,
                                root: true,
                                items: getItemsForFilter(categoria, categorie)
                            });
                        }
                        else {
                            filters.push({
                                type: 'categories',
                                slug: 'categories',
                                name: name_categorie,
                                root: false,
                                items: getItemsForFilter(categoria, categorie),
                            });
                        }

                        let marcheItems = r[2];
                        if (!marcheItems.length || marcheItems.length > 20) {
                            filtersDef.splice(2, 1);
                        }
                        else {
                            // @ts-ignore
                            filtersDef[2].items = marcheItems.map(item => ({
                                    slug: item.codice_marca,
                                    name: item.descrizione
                            }));
                        }

                        let mergedFilters = [...filtersByCustomList, ...filtersByRange, ...filtersByText];
                        mergedFilters.sort((a,b) => a.order - b.order);
                        for (let f of mergedFilters)
                            filtersDef.push(f);

                        this.creaFiltri(filtersDef, items).forEach(x => filters.push(x));

                        let fOthers = filters.find(f => f.slug == 'other');
                        // @ts-ignore
                        fOthers.items = fOthers.items.concat(filtersCheckbox);
                        filters.forEach(filter => {
                            if (filter.slug in filterValues && 'value' in filter) {
                                filter.value = this.parseFilterValue(filter, filterValues[filter.slug]);
                            }
                        });

                        const pages = Math.ceil(totalCount / limit);
                        const from = (page - 1) * limit + 1;
                        const to = Math.max(Math.min(page * limit, totalCount), from);

                        const total = totalCount;

                        for (let item of items) {
                            item.cdn_url = scaleImg(item.cdn_url, 400, 400);
                        }

                        const response: ArticoliList = {
                            items,
                            search: options.search,
                            page,
                            limit,
                            total,
                            pages,
                            from,
                            to,
                            sort,
                            filters,
                            filterValues,
                        };
                        return response;
                    }));
                })
        );  
    }

    getArticoliOfCategoriaByKeyword(categoria, keyword, extra_params?) {
        if (!extra_params)
            extra_params = {};

        return this.http.get(api_base_url + `Articoli/Azienda/${categoria}/Search`, {
            headers: this.getArticoliHeader(),
            params: {
                'ricerca': keyword,
                ...extra_params
            },
            observe: 'response'
        }).pipe(catchError(err => of([])));
    }

    getProductsOfCategory(id, extra_params?) {
        if (!extra_params)
            extra_params = {};

        if (!id)
            id = '';

        let ids = id.split('-');
        if (ids.length > 0 && ids[0].length)
            extra_params['famiglia'] = ids[0];
        if (ids.length > 1)
            extra_params['sottofamiglia'] = ids[1];
        if (ids.length > 2)
            extra_params['gruppo'] = ids[2];
        if (ids.length > 3)
            extra_params['sottogruppo'] = ids[3];

        return this.http.get(api_base_url + 'Articoli', {
            headers: this.getArticoliHeader(),
            params: extra_params,
            observe: 'response'
        }).pipe(catchError(err => of([])));
    }

    getArticoliByKeyword(keyword, extra_params?) {
        if (!extra_params)
            extra_params = {};

        if (!keyword)
            return of([]);

        return this.http.get(api_base_url + 'Articoli/Azienda/Search', {
            headers: this.getArticoliHeader(),
            params: {
                'ricerca': keyword,
                ...extra_params
            },
            observe: 'response'
        }).pipe(catchError(err => of([])));
    }

    getArticoliInPromo(id, keyword, extra_params) {
        if (!extra_params)
            extra_params = {};

        if (!id)
            id = '';

        let ids = id.split('-');
        if (ids.length > 0 && ids[0].length)
            extra_params['famiglia'] = ids[0];
        if (ids.length > 1)
            extra_params['sottofamiglia'] = ids[1];
        if (ids.length > 2)
            extra_params['gruppo'] = ids[2];
        if (ids.length > 3)
            extra_params['sottogruppo'] = ids[3];

        if (keyword)
            extra_params['[descart]'] = `#${keyword}#`;

        return this.http.get(api_base_url + 'Articoli/Promo', {
            headers: this.getArticoliHeader(),
            params: extra_params,
            observe: 'response'
        }).pipe(catchError(err => of([])));
    }

    getArticoli(categoria, keyword, params?) {
        if (keyword)
            keyword = keyword.trim();

        let promo;
        if (params) {
            promo = params['promo'];
            delete params['promo'];
        }

        if (promo)
            return this.getArticoliInPromo(categoria, keyword, params);

        if (categoria && keyword)
            return this.getArticoliOfCategoriaByKeyword(categoria, keyword, params);

        if (keyword)
            return this.getArticoliByKeyword(keyword, params);
        
        return this.getProductsOfCategory(categoria, params);
    }

    creaFiltri(filtersDef, products) {
        const result = [];

        filtersDef.forEach(filterDef => {
            if (filterDef.type === 'range') {
                result.push({
                    type: filterDef.type,
                    slug: filterDef.slug,
                    name: filterDef.name,
                    value: ["", ""],
                });
            } else if (filterDef.type == 'text') {
                result.push({
                    type: filterDef.type,
                    slug: filterDef.slug,
                    name: filterDef.name,
                    value: ''
                });
            } else if (filterDef.type === 'search') {
                result.push({
                    type: filterDef.type,
                    slug: filterDef.slug,
                    name: filterDef.name,
                    value: ''
                });
            } else if (filterDef.type === 'check' || filterDef.type === 'radio' || filterDef.type === 'color') {
                const itemsBySlug: { [slug: string]: FilterItem } = {};
                let items: FilterItem[] = [];

                if (filterDef.slug == 'other') {
                    result.push({
                        type: filterDef.type,
                        slug: filterDef.slug,
                        name: filterDef.name,
                        value: [],
                        items: [{
                            slug: 'promo',
                            name: 'In offerta',
                        },
                        {
                            slug: 'disponibili',
                            name: 'Solo disponibili'
                        },
                        {
                            slug: 'in_evidenza',
                            name: 'In evidenza'
                        }]
                    });
                    return;
                }
                else if (filterDef.type == 'radio') {
                    result.push({
                        type: filterDef.type,
                        slug: filterDef.slug,
                        name: filterDef.name,
                        value: null,
                        items: filterDef.items
                    });
                    return;
                };

                products.forEach(product => {
                    this.getListValues(filterDef.slug, product).forEach(value => {
                        if (value.slug in itemsBySlug) {
                            return;
                        }

                        const item: FilterItem = this.makeFilterItem(filterDef.type, value);

                        itemsBySlug[value.slug] = item;
                        items.push(item);
                    });
                });

                if (items.length < 1 || (filterDef.type === 'radio' && items.length < 2)) {
                    return;
                }

                result.push({
                    type: filterDef.type,
                    slug: filterDef.slug,
                    name: filterDef.name,
                    value: filterDef.type === 'radio' ? items[0].slug : [],
                    items,
                });
            }
        });

        return result;
    }


    getRangeValue(slug: string, product: Articolo, defaultValue: number = null): number {
        return this.getFilterValue('range', slug, product, defaultValue);
    }

    getListValues(slug: string, product: Articolo, defaultValue: FilterListValueDef[] = []): FilterListValueDef[] {
        return this.getFilterValue('check', slug, product, defaultValue);
    }


    getFilterValue(type: 'range', slug: string, product: Articolo, defaultValue: number): number;
    getFilterValue(type: 'check', slug: string, product: Articolo, defaultValue: FilterListValueDef[]): FilterListValueDef[];
    getFilterValue(type: string, slug: string, product: Articolo, defaultValue: FilterValueDef = null): FilterValueDef {
        if (type === 'range' && slug === 'price') {
            return this.priceArticolo.prezzoArticolo(product);
        } else if (type === 'check' && slug === 'discount') {
            const items = [
                { slug: 'any', name: 'Tutti' },
            ];

            if (this.priceArticolo.hasDiscount(product)) {
                items.push({ slug: 'yes', name: 'Si' });
            } else {
                items.push({ slug: 'no', name: 'No' });
            }

            return items;
        }

        return defaultValue;
    }

    makeFilterItem(filterType: 'check' | 'radio', value: FilterListValueDef): FilterItem | ColorFilterItem {
        switch (filterType) {
            case 'check':
            case 'radio':
                return {
                    slug: value.slug,
                    name: value.name,
                    count: 0,
                };
        }
    }

    parseFilterValue(filter: Filter, value: string): any {
        switch (filter.type) {
            case 'range':
                return value.split('-');
            case 'check':
            case 'color':
                return value.trim() === '' ? [] : value.split(',').map(x => x.trim());
        }
    
        return value;
    }

    marcheCached = {};
    getMarche(classificazione) : Observable<any[]> {
        if (!classificazione)
            return of([]);

        if (this.marcheCached[classificazione])
            return of(this.marcheCached[classificazione]);

        let params = {};
        if (classificazione) {
            let param = ['famiglia', 'sottofamiglia', 'gruppo', 'sottogruppo'];
            let codes = classificazione.split('-');
            for (let i = 0; i < codes.length; i++) {
                params[param[i]] = codes[i];
            }
        }

        return this.http.get<any[]>(api_base_url + `productsearch/search/brands`, {
            params, 'headers': {...this.getBusinessHeader(), ...this.store.x_canale}
        }).pipe(
            catchError(err => of([])),
            map(marche => marche.sort((a,b) => a.descrizione.localeCompare(b.descrizione))),
            tap(marche => this.marcheCached[classificazione] = marche)
        );
    }

    // NOTA: endpoint "Collections" eliminato dal backoffice (risponde 404). Codice commentato il 2026-07-29,
    // lasciato per riferimento futuro nel caso torni disponibile un nuovo endpoint equivalente.
    // getCollections() : Observable<Collection[]> {
    //     return this.http.get<Collection[]>(api_base_url + 'Collections',
    //     {headers: this.getBusinessHeader()}).pipe(catchError(err => of([])));
    // }

    // getCollectionById(id) : Observable<Collection> {
    //     return this.getCollections().pipe(map<Collection[], Collection>(collections => collections.find(collection => String(collection.id) == String(id))));
    // }

    // getArticoliOfCollection(id, extra_params?) {
    //     if (!extra_params)
    //         extra_params = {};

    //     return this.http.get(api_base_url + `Collections/${id}`, {
    //         headers: this.getArticoliHeader(),
    //         params: extra_params,
    //         observe: 'response'
    //     }).pipe(catchError(err => of([])));
    // }

    // getCollectionList(id, options: ListOptions): Observable<ArticoliList> {
    //     const page = options.page || 1;
    //     const limit = options.limit || this.store.columns * 3;
    //     const sort = options.sort || 'default';
    //     const filterValues = options.filterValues || {};
    //     const filters: Filter[] = [];
    //     const filtersDef = [
    //         { type: 'search', slug: 'search', name: 'Ricerca'},
    //         { type: 'range', slug: 'price', name: 'Prezzo' }
    //     ];

    //     let extra_params = {
    //         'page': page,
    //         'page_result': limit,
    //     }

    //     if (sort == 'price_asc')
    //         extra_params['order_by'] = '>prezzo_originale';
    //     else if (sort == 'price_desc')
    //         extra_params['order_by'] = '<prezzo_originale';

    //     if (filterValues['price']) {
    //         let [min, max] = filterValues['price'].split('-');
    //         extra_params['[prezzo_originale]'] = [];
    //         if (min)
    //             extra_params['[prezzo_originale]'].push('>' + min);
    //         if (max)
    //             extra_params['[prezzo_originale]'].push('<' + max);
    //     }

    //     let search = filterValues['search'];
    //     if (search && search.trim()) {
    //         extra_params['[descart]'] = '#' + search.trim() + '#';
    //     }

    //     let articoli = this.getArticoliOfCollection(id, extra_params);

    //     return forkJoin([this.categoria.categorie$.pipe(take(1)), articoli])
    //         .pipe(map<any, ArticoliList>(r => {
    //             let items = r[1].body || [];
    //             let totalCount = 0;
    //             try {
    //                 totalCount = r[1].headers.get('x-totalcount') || 0;
    //             }
    //             catch (err) { }

    //             this.creaFiltri(filtersDef, items).forEach(x => filters.push(x));

    //             filters.forEach(filter => {
    //                 if (filter.slug in filterValues && 'value' in filter) {
    //                     filter.value = this.parseFilterValue(filter, filterValues[filter.slug]);
    //                 }
    //             });

    //             const pages = Math.ceil(totalCount / limit);
    //             const from = (page - 1) * limit + 1;
    //             const to = Math.max(Math.min(page * limit, totalCount), from);

    //             const total = totalCount;

    //             for (let item of items) {
    //                 item.cdn_url = scaleImg(item.cdn_url, 400, 400);
    //             }

    //             const response: ArticoliList = {
    //                 items,
    //                 search: options.search,
    //                 page,
    //                 limit,
    //                 total,
    //                 pages,
    //                 from,
    //                 to,
    //                 sort,
    //                 filters,
    //                 filterValues,
    //             };
    //             return response;
    //         }));
    // }


    getLayout() : Observable<DynamicElement[]> {
        const id_azienda = this.store.identificativo_azienda;
        const tipo = this.store.price == 'b2c' ? 0 : 1;
        return this.http.get<any>(api_base_url + `theme/${id_azienda}/${tipo}/layout`, {
            headers: {
                'Business': this.store.azienda_nome_univoco
            }
        });
    }

    setLayout(layout) {
        const tipo = this.store.price == 'b2c' ? 0 : 1;
        return this.http.post(api_base_url +  `theme/${tipo}/layout`, layout, {
            headers: {
                ...this.getBusinessHeader()
            }
        });
    }

    setCSS(css) {
        const tipo = this.store.price == 'b2c' ? 0 : 1;
        return this.http.post(api_base_url +  `theme/${tipo}/css`, css, {
            headers: {
                ...this.getBusinessHeader()
            },
            responseType: 'text'
        });
    }

    extLists = {};

    getExtList(list_num) : Observable<ListElement[]> {
        if (this.extLists[list_num])
            return of({...this.extLists[list_num]});
        return this.http.get<ListElement[]>(api_base_url + `${this.store.identificativo_azienda}/estensioneliste/${list_num}`, {
            headers: this.getBusinessHeader()
        }).pipe(
            catchError(err => {
                return of([]);
            }),
            tap(extList => this.extLists[list_num] = extList)
        );
    }

    private extra_fields : any[];

    getExtraFields() : Observable<any[]> {
        if (this.extra_fields)
            return of(this.extra_fields);
        return this.http.get<any[]>(api_base_url + 'Articoli/ExtraFieldsInfo', {
            headers: this.getBusinessHeader()
        }).pipe(
            catchError(err => of([])),
            map(extra => extra.map(col => 
                {
                    let filterDef : any = {
                        slug: col.data,
                        name: col.title,
                        order: col.ordinamento
                    };

                    if (col.isList && col.isCustomList) {
                        filterDef['type'] = 'radio';
                        filterDef['items'] = this.getExtList(col.optionList);
                        return filterDef;
                    }

                    if (col.data.includes('string')) {
                        filterDef['type'] = 'text';
                        return filterDef;
                    }
                    else if (col.isCheck) {
                        filterDef['type'] = 'other';
                        return filterDef;
                    }
                    else {
                        filterDef['type'] = 'range';
                        filterDef['int'] = col.data.includes('int');
                        return filterDef;
                    }

                })
            ),
            mergeMap(extra_fields => {
                let filtersByCustomList = extra_fields.filter(extra => extra.items);
                if (filtersByCustomList.length) {
                    let bunch = {};
                    for (let f of filtersByCustomList)
                        bunch[f.slug] = f.items;
                    return forkJoin(bunch).pipe(
                        tap(lists => {
                            for (let f of filtersByCustomList) {
                                // @ts-ignore
                                f.items = lists[f.slug].map(item => ({
                                        slug: '' + item.identificativo_campo,
                                        name: item.descrizione_campo
                                })).sort((a,b) => a.name.localeCompare(b.name))
                            }
                        }),
                        map(lists => extra_fields)
                    );
                }

                return of(extra_fields);
            }),
            tap(extra => this.extra_fields = extra)
        )
    }

    getAvailableCoupons(importo_minimo?) {
        let params = {};
        if (importo_minimo)
            params['importo'] = importo_minimo;
        return this.http.get(api_base_url + 'Coupon/Cliente', {params, headers: this.getBusinessHeader()});
    }

}

export interface ListElement {
    identificativo_campo: number,
    descrizione_campo: string,
    img: string
}
